import React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MUICard from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import NumberTextfield from 'components/uncontrolled/NumberTextfield';
import Textfield from 'components/uncontrolled/Textfield';
import PasswordTextfield from 'components/uncontrolled/PasswordTextfield';
import Select from 'components/uncontrolled/Select';
import Typography from '@mui/material/Typography';
import Logo from 'assets/WorkforceHubLogo.svg';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// ------------------------------------------------------------- //
// ------------------------------------------------------------- //
//
//
// Styled Components
// For Free Trial and Free Trial Form
//
//
// ------------------------------------------------------------- //
// ------------------------------------------------------------- //

export const LogoImage = styled((props) => (
  <img {...props} alt="WorkforceHub Logo" src={Logo} />
))({
  width: 64,
  height: 64,
});

export const WarningImage = styled((props) => <WarningAmberIcon {...props} />)(
  ({ theme }) => ({
    color: theme.palette.warning.main,
    width: 64,
    height: 64,
  })
);

export const PageTitle = styled((props) => (
  <Typography variant="h2" {...props} />
))(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.text.primary,
  textWrap: 'balance',
}));

export const PageSubtitle = styled((props) => (
  <Typography variant="body1" component="p" {...props} />
))(({ theme }) => ({
  lineHeight: 1.7,
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightMedium,
  paddingLeft: theme.spacing(0),
  paddingRight: theme.spacing(0),
  textWrap: 'balance',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  flex: 1,
  '& > span': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
  },
  '& > span.attention': {
    ...theme.typography.h5,
    color: theme.palette.text.primary,
  },
}));

export const PageBlurb = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  // color: theme.palette.text.primary,
  color: theme.palette.text.secondary,
  paddingLeft: theme.spacing(0),
  paddingRight: theme.spacing(0),
  flex: 1,
}));

export const GridMain = styled((props) => <Box component="main" {...props} />)(
  ({ theme }) => ({
    display: 'grid',
    height: '100%',
    minHeight: '100svh', // Full viewport height
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
        'main'
        `,
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: `minmax(0, 1fr) ${theme.breakpoints.values.lg}px minmax(0, 1fr)`,
      margin: '0 auto',
      gridTemplateAreas: `
        'leading main trailing'
        `,
    },
  })
);

export const CardWrapper = styled((props) => <Box {...props} />)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
}));

export const Card = styled((props) => <MUICard {...props} />)(({ theme }) => ({
  height: 'inherit',
  width: '100%',
  maxWidth: theme.breakpoints.values.md,
  padding: theme.spacing(2),
  marginBottom: 256, // Not sure we want this but a little space at the bottom is nice to offset from dead center
  [theme.breakpoints.down('sm')]: {
    boxShadow: 'none',
    borderRadius: 0,
    height: '100%',
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));

export const BoxCenter = styled((props) => <Box {...props} />)(({ theme }) => ({
  gridArea: 'main',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  [theme.breakpoints.down('sm')]: {
    backgroundColor: theme.palette.background.paper,
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));

export const BoxLeading = styled((props) => (
  <Box component="aside" {...props} />
))(({ theme }) => ({
  gridArea: 'leading',
  flexGrow: 1,
  display: 'none',
  height: '100%',
  // backgroundColor: theme.palette.custom.grey[100],
  backgroundColor: theme.palette.primary.main,
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
  },
}));

export const BoxTrailing = styled((props) => (
  <Box component="aside" {...props} />
))(({ theme }) => ({
  gridArea: 'trailing',
  flexGrow: 1,
  display: 'none',
  height: '100%',
  // backgroundColor: theme.palette.custom.grey[100],
  backgroundColor: theme.palette.primary.main,
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
  },
}));

export const FormInputGrid = styled((props) => <Box {...props} />)(
  ({ theme }) => ({
    display: 'grid',
    gridTemplateAreas: `
    'header'
    'firstname'
    'lastname'
    'company'
    'employees'
    'business'
    'phone'
    'divider'
    'footer'
    `,
    gridTemplateColumns: '1fr',
    gap: theme.spacing(4),
    columnGap: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      gridTemplateAreas: `
      'header header'
      'firstname lastname'
      'company employees'
      'business phone'
      'divider divider'
      'footer footer'
      `,
      gridTemplateColumns: '1fr 1fr',
    },
  })
);

export const BasicInfoFormInputGrid = styled((props) => <Box {...props} />)(
  ({ theme }) => ({
    display: 'grid',
    gridTemplateAreas: `
    'header'
    'company'
    'state'
    'timezone'
    'divider'
    'subheader'
    'password'
    'confirmpassword'
    'divider2'
    'footer'
    `,
    gridTemplateColumns: '1fr',
    gap: theme.spacing(4),
    columnGap: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      gridTemplateAreas: `
      'header header'
      'company company'
      'state timezone'
      'divider divider'
      'subheader subheader'
      'password password'
      'confirmpassword confirmpassword'
      'divider2 divider2'
      'footer footer'
      `,
      gridTemplateColumns: '1fr 1fr',
    },
  })
);

export const FormInputHeader = styled(Box)(({ theme }) => ({
  gridArea: 'header',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  ...theme.typography.h3,
  '& > span': {
    ...theme.typography.body1,
    color: theme.palette.text.secondary,
  },
  '& > span.error': {
    ...theme.typography.body1,
    color: theme.palette.error.main,
  },
}));

export const FormInputSubHeader = styled(Box)(({ theme }) => ({
  gridArea: 'subheader',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  ...theme.typography.h5,
  '& > span': {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    display: 'block',
  },
  '& > span.password-email': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

export const FormInputFooter = styled(Box)(({ theme }) => ({
  gridArea: 'footer',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'rows',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
}));

export const FormDivider = styled(Divider)(({ theme }) => ({
  gridArea: 'divider',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const FirstNameField = styled(Textfield)(() => ({
  gridArea: 'firstname',
}));

export const LastNameField = styled(Textfield)(() => ({
  gridArea: 'lastname',
}));

export const CompanyField = styled(Textfield)(() => ({
  gridArea: 'company',
}));

export const NumberOfEmployeesField = styled(NumberTextfield)(() => ({
  gridArea: 'employees',
}));

export const BusinessEmailField = styled(Textfield)(() => ({
  gridArea: 'business',
}));

export const PhoneField = styled(NumberTextfield)(() => ({
  gridArea: 'phone',
}));

export const StateSelect = styled(Select)(() => ({
  gridArea: 'state',
}));

export const TimezoneSelect = styled(Select)(() => ({
  gridArea: 'timezone',
}));

export const PasswordField = styled(PasswordTextfield)(() => ({
  gridArea: 'password',
}));

export const ConfirmPasswordField = styled(PasswordTextfield)(() => ({
  gridArea: 'confirmpassword',
}));

export const PageTransition = styled(
  React.forwardRef((props, ref) => <Box ref={ref} {...props} />)
)(({ theme }) => ({
  position: 'relative',
  transition: `opacity ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}, transform ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
  '&.page-enter': {
    opacity: 0,
    transform: `scale(1.1)`,
  },

  '&.page-enter-active': {
    opacity: 1,
    transform: `scale(1)`,
  },

  '&.page-exit': {
    opacity: 1,
    transform: `scale(1)`,
  },

  '&.page-exit-active': {
    opacity: 0,
    transform: `scale(0.9)`,
  },
}));
